import { z } from 'zod/v4';

import {
    buttonHorizontalPositionSchema,
    buttonSizeSchema,
    buttonStyleTypeSchema,
    socialAppearanceSchema,
    socialIconTypeSchema,
    socialNetworkSchema,
    themeButtonDataPartialSchemaLegacy,
} from '@sites/entity-core';

/** Icon size in px. Absurdity guard only — the host UI enforces no bound (indent's policy). */
const HEIGHT_MIN = 8;
const HEIGHT_MAX = 512;

/**
 * One entry as the AI tools accept it. Deliberately NARROWER than entity-core's `socialItemSchema`:
 * `type` and `iconType` are omitted here even though they exist on the persisted item, because they
 * are SHARED appearance — one look for the whole group. Exposing them per item would invite a state
 * the editor can't produce and the renderer disagrees about: the css-vars read `data.type`/
 * `data.styleType` while the template reads `social.type`/`social.iconType`, so the two must always
 * agree. They're offered once at the data level instead, and the mutation layer mirrors them onto
 * every entry exactly as `SocialControlComponent.onSwitchButtonType`/`setIconType` do.
 *
 * `socialNetwork` and `link` are required: an entry with neither points nowhere, and entity-core's
 * factory default (a Telegram icon with an EMPTY link) is a placeholder the editor expects a human
 * to fill in — fine for a drag-and-drop, useless as a tool result.
 */
export const partialSocialItemSchema = z.object({
    socialNetwork: socialNetworkSchema.describe('Which network/messenger this entry links to.'),
    link: z
        .string()
        .min(1)
        .describe(
            'Destination — a full profile URL, or the handle/phone/email for networks that take one ' +
                '(telegram, whatsapp, viber, skype, email, phone). The published site rewrites handles into ' +
                'proper deep links.'
        ),
    name: z
        .string()
        .optional()
        .describe('Label shown in the button appearance. Defaults to the network id when omitted.'),
    description: z.string().optional().describe('Optional secondary caption.'),
    preventLinkConverting: z
        .boolean()
        .optional()
        .describe('Skip the published site’s link rewriting and use the link verbatim.'),
});

/**
 * THE social widget data shape as AI tools accept it — single source of truth shared by
 * `create_social_widget`/`update_social_widget`, so their field surface can never drift apart (same
 * reasoning as the button/text/image/indent twins). Lives here because both `apps/mcp-backend`'s
 * mutations and `apps/mcp-server`'s tools need the exact same shape, and neither may import the
 * other.
 *
 * Everything except `socials` is group-level appearance. `buttonBase` is a DEEP partial reusing
 * entity-core's own sub-schema (button's precedent) so a model recolouring the group isn't forced to
 * resupply border/radius it never touched; the mutation layer merges it onto the current value and
 * flips `styleType` to `'custom'`, without which the renderer ignores it entirely.
 */
export const partialSocialWidgetDataSchema = z.object({
    socials: z
        .array(partialSocialItemSchema)
        .min(1)
        .optional()
        .describe(
            'The entries in this widget. REPLACES the whole list — include every entry you want to keep, ' +
                'not just the new one.'
        ),

    // --- shared appearance: mirrored onto every entry by the mutation layer ---
    type: socialAppearanceSchema
        .optional()
        .describe(
            'How every entry is rendered: "icon" (default) for bare icons, "button" for labelled buttons. ' +
                '"iconText" is legacy.'
        ),
    iconType: socialIconTypeSchema
        .optional()
        .describe('Icon style for every entry, e.g. "round" (default), "logos", "square", "flat", "outlined".'),

    // --- shared appearance: data-level only ---
    styleType: buttonStyleTypeSchema
        .optional()
        .describe(
            'Theme preset for the button appearance. Set "custom" to use buttonBase — supplying buttonBase ' +
                'without a styleType switches to "custom" automatically.'
        ),
    height: z
        .number()
        .int()
        .min(HEIGHT_MIN)
        .max(HEIGHT_MAX)
        .optional()
        .describe('Icon size in pixels (default 48). Only affects the "icon" appearance.'),
    buttonSize: buttonSizeSchema.optional().describe('Button size for the "button" appearance: "S", "M" or "L".'),
    horizontalAlign: buttonHorizontalPositionSchema
        .optional()
        .describe('Group alignment inside the column: "L" (left), "M" (center) or "R" (right).'),
    buttonBase: themeButtonDataPartialSchemaLegacy
        .optional()
        .describe(
            'Custom colours/border for the button appearance — partial, only the keys you provide change, ' +
                'e.g. { "background": "#1d4ed8", "color": "#ffffff" }.'
        ),

    hideOnDesktop: z.boolean().optional().describe('Hide this widget on desktop screens.'),
    hideOnTablet: z.boolean().optional().describe('Hide this widget on tablet screens.'),
    hideOnMobile: z.boolean().optional().describe('Hide this widget on mobile screens.'),
    isSynchronizeStyle: z
        .boolean()
        .optional()
        .describe('Whether tablet/mobile styling stays synchronized with the desktop styling.'),
});

export type PartialSocialWidgetData = z.infer<typeof partialSocialWidgetDataSchema>;
export type PartialSocialItem = z.infer<typeof partialSocialItemSchema>;
